import { useWizard } from '../../../context/WizardContext';
import { NavigationButtons } from '../../../components/wizard/NavigationButtons';
import type { QuestionVersion } from '../../../types/wizard';
import { cn } from '../../../lib/utils';
import { useTranslation } from '../../../context/LanguageContext';
import type { TranslationKey } from '../../../i18n';
import { Check, Languages } from 'lucide-react';

// These name the language of the *generated paper*, so each is written in the
// language it selects and stays legible whatever the interface language is. No
// flags: a script is not a country, and neither Bangla nor English maps to one.
const VERSIONS: {
  value: QuestionVersion;
  labelKey: TranslationKey;
  sublabelKey: TranslationKey;
  gradient: string;
  selected: string;
  selectedText: string;
}[] = [
  {
    value: 'Bangla',
    labelKey: 'wizard.versionBangla',
    sublabelKey: 'wizard.versionBanglaSub',
    gradient: 'from-emerald-400 to-emerald-600',
    selected: 'border-emerald-500/50 bg-emerald-500/10',
    selectedText: 'text-emerald-300',
  },
  {
    value: 'English',
    labelKey: 'wizard.versionEnglish',
    sublabelKey: 'wizard.versionEnglishSub',
    gradient: 'from-brand-500 to-violet-600',
    selected: 'border-violet-500/50 bg-violet-500/10',
    selectedText: 'text-violet-300',
  },
];

export function Step0Version() {
  const { state, setVersion } = useWizard();
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white">{t('wizard.versionTitle')}</h2>
        <p className="text-sm text-slate-500 mt-1">{t('wizard.versionSubtitle')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {VERSIONS.map((v) => {
          const isSelected = state.selectedVersion === v.value;
          return (
            <button
              key={v.value}
              type="button"
              onClick={() => setVersion(v.value)}
              className={cn(
                'relative flex flex-col items-center gap-4 rounded-2xl border-2 p-8 transition-all duration-200 focus:outline-none group',
                isSelected
                  ? `${v.selected} shadow-glow`
                  : 'border-slate-700 bg-slate-700/50 hover:border-slate-600 hover:shadow-card-hover hover:-translate-y-0.5'
              )}
            >
              {isSelected && (
                <span className="absolute top-3 right-3 flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 text-white">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
              )}
              <div
                className={cn(
                  'flex h-16 w-16 items-center justify-center rounded-2xl text-white shadow-md transition-transform duration-300 group-hover:scale-105',
                  `bg-gradient-to-br ${v.gradient}`
                )}
              >
                <Languages className="h-7 w-7" />
              </div>
              <div className="text-center">
                <p className={cn('text-xl font-bold', isSelected ? v.selectedText : 'text-white')}>
                  {t(v.labelKey)}
                </p>
                <p className="text-sm text-slate-500 mt-1">{t(v.sublabelKey)}</p>
              </div>
            </button>
          );
        })}
      </div>

      <NavigationButtons />
    </div>
  );
}
